import React, { useState, useEffect, ReactNode } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WizardStep {
  id: string;
  title: string;
  description?: string;
  content: ReactNode;
  validate?: () => Promise<boolean> | boolean;
}

interface WizardFormProps {
  methods: UseFormReturn<any>;
  onSubmit: (data: any) => void;
  steps: WizardStep[];
  submitText?: string;
  cancelText?: string;
  onCancel?: () => void;
  isSubmitting?: boolean;
  className?: string;
  onStepChange?: (stepIndex: number) => void;
  showStepNumbers?: boolean;
  useUrlParams?: boolean;
  urlParamName?: string;
  canResume?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const WizardForm: React.FC<WizardFormProps> = ({
  methods,
  onSubmit,
  steps,
  submitText = 'Submit',
  cancelText = 'Cancel',
  onCancel,
  isSubmitting = false,
  className = '',
  onStepChange,
  showStepNumbers = true,
  useUrlParams = false,
  urlParamName = 'step',
  canResume = false,
}) => {
  const [currentStep, setCurrentStep] = useState(() => {
    if (canResume && useUrlParams && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const stepFromUrl = params.get(urlParamName);
      if (stepFromUrl) {
        const stepIndex = steps.findIndex((s) => s.id === stepFromUrl);
        if (stepIndex !== -1) return stepIndex;
      }
    }
    return 0;
  });

  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  // Reset to step 0 if canResume is false so stale URL params don't persist
  useEffect(() => {
    if (!canResume && useUrlParams && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.has(urlParamName)) {
        params.delete(urlParamName);
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}?${params.toString()}`,
        );
        setCurrentStep(0);
      }
    }
  }, [canResume, useUrlParams, urlParamName]);

  const updateUrlParam = (stepIndex: number) => {
    if (useUrlParams && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      params.set(urlParamName, steps[stepIndex].id);
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?${params.toString()}`,
      );
    }
  };

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const currentStepData = steps[currentStep];

  const handleNext = async () => {
    if (currentStepData.validate) {
      const isValid = await currentStepData.validate();
      if (!isValid) return;
    }
    setCompletedSteps((prev) => new Set(prev).add(currentStep));
    if (!isLastStep) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      updateUrlParam(nextStep);
      onStepChange?.(nextStep);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      updateUrlParam(prevStep);
      onStepChange?.(prevStep);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex <= currentStep || completedSteps.has(stepIndex - 1)) {
      setCurrentStep(stepIndex);
      updateUrlParam(stepIndex);
      onStepChange?.(stepIndex);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    methods.handleSubmit(onSubmit)(e);
  };

  return (
    <FormProvider {...methods}>
      <div
        className={cn(
          'flex flex-col flex-1 min-h-0 overflow-hidden',
          className,
        )}
      >
        {/* ── Step indicator ───────────────────────────────────────────────── */}
        <div className="flex-shrink-0 px-5 py-4 border-b border-border bg-muted/30">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const isActive = index === currentStep;
              const isCompleted = completedSteps.has(index);
              const isAccessible =
                index <= currentStep || completedSteps.has(index - 1);

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center flex-1">
                    <button
                      type="button"
                      onClick={() => handleStepClick(index)}
                      disabled={!isAccessible}
                      className={cn(
                        'w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                        isActive &&
                          'bg-primary text-primary-foreground ring-4 ring-primary/20',
                        isCompleted &&
                          !isActive &&
                          'bg-primary/15 text-primary hover:bg-primary/25',
                        !isActive &&
                          !isCompleted &&
                          'bg-muted text-muted-foreground',
                        isAccessible &&
                          !isActive &&
                          'cursor-pointer hover:bg-muted-foreground/10',
                        !isAccessible && 'opacity-40 cursor-not-allowed',
                      )}
                    >
                      {isCompleted && !isActive ? (
                        <Check className="h-4 w-4" />
                      ) : showStepNumbers ? (
                        index + 1
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-current" />
                      )}
                    </button>
                    <div className="mt-1.5 text-center hidden sm:block">
                      <span
                        className={cn(
                          'text-[11px] font-medium',
                          isActive
                            ? 'text-foreground'
                            : 'text-muted-foreground',
                        )}
                      >
                        {step.title}
                      </span>
                    </div>
                  </div>

                  {index < steps.length - 1 && (
                    <div
                      className={cn(
                        'h-px flex-1 mx-2 transition-colors',
                        isCompleted ? 'bg-primary' : 'bg-border',
                      )}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overflow-hidden min-w-0 px-5 pt-5 pb-8 space-y-5">
          <div className="sm:hidden space-y-1">
            <h3 className="text-base font-semibold text-foreground">
              {currentStepData.title}
            </h3>
            {currentStepData.description && (
              <p className="text-xs text-muted-foreground">
                {currentStepData.description}
              </p>
            )}
          </div>

          <div className="hidden sm:block space-y-1">
            <h3 className="text-base font-semibold text-foreground">
              {currentStepData.title}
            </h3>
            {currentStepData.description && (
              <p className="text-xs text-muted-foreground">
                {currentStepData.description}
              </p>
            )}
          </div>

          {currentStepData.content}
        </div>

        <div className="flex-shrink-0 border-t border-border bg-background px-4 py-3">
          <div className="flex items-center gap-2">
            {isFirstStep && onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
                className="cursor-pointer"
              >
                {cancelText}
              </Button>
            )}
            {!isFirstStep && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={isSubmitting}
                className="cursor-pointer"
              >
                <ChevronLeft className="mr-1.5 h-4 w-4" />
                Back
              </Button>
            )}

            <div className="flex-1" />

            {!isLastStep ? (
              <Button
                type="button"
                onClick={handleNext}
                disabled={isSubmitting}
                className="bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
              >
                Next
                <ChevronRight className="ml-1.5 h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleFormSubmit}
                disabled={isSubmitting}
                className="bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />{' '}
                    Submitting...
                  </>
                ) : (
                  submitText
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </FormProvider>
  );
};
